import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatCard, SectionCard, StatusBadge, TierBadge, Empty } from "@/components/shared";
import { num, fmtDateTime } from "@/lib/format";
import { Gift, Coins, Clock, Award, CheckCircle2, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";

const TABS = ["Overview", "Points Ledger", "Reward Catalogue", "Redemptions", "Rules"];

export default function Loyalty() {
  const [tab, setTab] = useState("Overview");
  const [sum, setSum] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [reds, setReds] = useState([]);
  const [rules, setRules] = useState(null);
  const [partners, setPartners] = useState([]);
  const [redeemFor, setRedeemFor] = useState(null);

  const load = () => {
    api.get("/loyalty/summary").then((r) => setSum(r.data));
    api.get("/loyalty/ledger").then((r) => setLedger(r.data));
    api.get("/loyalty/rewards").then((r) => setRewards(r.data));
    api.get("/loyalty/redemptions").then((r) => setReds(r.data));
    api.get("/loyalty/rules").then((r) => setRules(r.data));
  };
  useEffect(() => { load(); api.get("/partners", { params: { status: "verified" } }).then((r) => setPartners(r.data)); }, []);

  const approve = async (l, action) => {
    try { const { data } = await api.post(`/loyalty/points/${l.id}/approve`, { action, reason: action === "reverse" ? "Reversed by Rewards Ops" : "" }); toast.success(data.status === "finance_review" ? (data.message || "Escalated to Finance") : action === "approve" ? `Approved · tier ${data.tier}` : "Reversed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const financeApprove = async (l, action) => {
    try { const { data } = await api.post(`/loyalty/points/${l.id}/finance-approve`, { action, reason: action === "reverse" ? "Rejected by Finance" : "" }); toast.success(action === "approve" ? `Finance approved · tier ${data.tier}` : "Finance rejected"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Loyalty & Rewards" subtitle="Points ledger with pending→approved gate, tiers, catalogue and redemptions." testid="loyalty-rewards-catalogue" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 stagger">
        <StatCard label="Approved Points" value={sum ? num(sum.earned) : "—"} sub="redeemable earned" accent="#059669" icon={Coins} testid="loy-earned" />
        <StatCard label="Pending Points" value={sum ? num(sum.pending) : "—"} sub="awaiting Rewards Ops" accent="#D97706" icon={Clock} testid="loy-pending" />
        <StatCard label="Redeemed" value={sum ? num(sum.burned) : "—"} sub="lifetime burns" accent="#DC2626" icon={Gift} testid="loy-burned" />
        <StatCard label="Active Members" value={sum ? num(sum.active_members) : "—"} sub="earning partners" accent="#0891B2" icon={Award} testid="loy-members" />
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-5 overflow-x-auto">
        {TABS.map((t) => <button key={t} data-testid={`ltab-${t.replace(/ /g, "-").toLowerCase()}`} onClick={() => setTab(t)} className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px ${tab === t ? "border-[var(--copper)] text-slate-900" : "border-transparent text-slate-500"}`}>{t}</button>)}
      </div>

      {tab === "Overview" && sum && (
        <div className="grid lg:grid-cols-2 gap-6">
          <SectionCard title="Tier Distribution">
            <div className="space-y-3">
              {Object.entries(sum.tier_distribution).map(([t, c]) => (
                <div key={t} className="flex items-center justify-between"><TierBadge tier={t} /><span className="font-mono font-semibold">{c} partners</span></div>
              ))}
            </div>
          </SectionCard>
          <SectionCard title="Pending Approval Queue">
            <p className="text-sm text-slate-500 mb-3">{ledger.filter((l) => l.status === "pending").length} entries awaiting Rewards Ops.</p>
            <div className="space-y-2 max-h-64 overflow-auto">
              {ledger.filter((l) => l.status === "pending").slice(0, 8).map((l) => (
                <div key={l.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-50">
                  <span className="text-slate-600 text-xs">{l.partner_code} · +{num(l.points)}</span>
                  <button onClick={() => approve(l, "approve")} className="text-xs text-emerald-600 font-semibold">Approve</button>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      )}

      {tab === "Points Ledger" && (
        <SectionCard className="!p-0" testid="points-ledger">
          <Tbl head={["Partner", "Type", "Points", "Status", "Reason", "Date", "Action"]} empty={!ledger.length}>
            {ledger.map((l) => (
              <tr key={l.id} className="border-b border-slate-50">
                <td className="px-5 py-3 font-mono text-xs">{l.partner_code}</td><td className="px-5 py-3 capitalize">{l.type}</td>
                <td className={`px-5 py-3 font-mono font-semibold ${l.type === "burn" || l.type === "reversal" ? "text-red-600" : "text-emerald-600"}`}>{l.type === "burn" || l.type === "reversal" ? "−" : "+"}{num(l.points)}</td>
                <td className="px-5 py-3"><StatusBadge status={l.status} /></td>
                <td className="px-5 py-3 text-xs text-slate-500 max-w-[220px] truncate">{l.reason}</td>
                <td className="px-5 py-3 text-xs text-slate-400">{fmtDateTime(l.timestamp)}</td>
                <td className="px-5 py-3">{l.status === "pending" && (
                  <div className="flex gap-1.5">
                    <button data-testid={`approve-points-${l.id}`} onClick={() => approve(l, "approve")} className="text-emerald-600" title="Approve"><CheckCircle2 size={16} /></button>
                    <button onClick={() => approve(l, "reverse")} className="text-red-500" title="Reverse"><RotateCcw size={15} /></button>
                  </div>
                )}{l.status === "finance_review" && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-semibold text-violet-700">FINANCE</span>
                    <button data-testid={`finance-approve-${l.id}`} onClick={() => financeApprove(l, "approve")} className="text-emerald-600" title="Finance approve"><CheckCircle2 size={16} /></button>
                    <button onClick={() => financeApprove(l, "reverse")} className="text-red-500" title="Finance reject"><RotateCcw size={15} /></button>
                  </div>
                )}</td>
              </tr>
            ))}
          </Tbl>
        </SectionCard>
      )}

      {tab === "Reward Catalogue" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reward-catalogue">
          {rewards.map((r) => (
            <div key={r.id} className="card-surface p-5 flex flex-col">
              <div className="flex justify-between items-start">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold uppercase">{r.category}</span>
                <span className="text-xs text-slate-400">{r.stock} in stock</span>
              </div>
              <h4 className="font-semibold text-slate-800 mt-3 flex-1">{r.name}</h4>
              <div className="flex items-center justify-between mt-4">
                <span className="font-mono text-lg font-extrabold text-[var(--copper)]">{num(r.points)} <span className="text-xs text-slate-400 font-normal">pts</span></span>
                <button data-testid={`redeem-${r.id}`} onClick={() => setRedeemFor(r)} className="text-sm text-white font-semibold px-3 py-1.5 rounded-lg" style={{ background: "var(--navy-800)" }}>Redeem</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Redemptions" && (
        <SectionCard className="!p-0" testid="redemptions-table">
          <Tbl head={["Partner", "Reward", "Points", "Status", "Date", "Action"]} empty={!reds.length}>
            {reds.map((r) => (
              <tr key={r.id} className="border-b border-slate-50">
                <td className="px-5 py-3">{r.partner_name} <span className="font-mono text-xs text-slate-400">{r.partner_code}</span></td>
                <td className="px-5 py-3">{r.reward_name}</td><td className="px-5 py-3 font-mono">{num(r.points)}</td>
                <td className="px-5 py-3"><StatusBadge status={r.status} /></td><td className="px-5 py-3 text-xs text-slate-400">{fmtDateTime(r.timestamp)}</td>
                <td className="px-5 py-3">
                  <select defaultValue={r.status} onChange={async (e) => { await api.post(`/loyalty/redemptions/${r.id}/action`, { status: e.target.value }); toast.success("Updated"); load(); }} className="text-xs border border-slate-200 rounded px-2 py-1 bg-white">
                    {["requested", "approved", "shipped", "delivered", "rejected"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </Tbl>
        </SectionCard>
      )}

      {tab === "Rules" && rules && <RulesEditor rules={rules} onSaved={load} />}

      {redeemFor && <RedeemDialog reward={redeemFor} partners={partners} onClose={() => setRedeemFor(null)} onDone={() => { setRedeemFor(null); load(); toast.success("Redemption requested"); }} />}
    </div>
  );
}

function RulesEditor({ rules, onSaved }) {
  const [ppm, setPpm] = useState(rules.points_per_mt || 100);
  const [auto, setAuto] = useState(rules.auto_approve || false);
  const [fin, setFin] = useState(rules.finance_approval_threshold_mt || 25);
  const [th, setTh] = useState(rules.tier_thresholds || { Silver: 0, Gold: 5000, Platinum: 15000, Diamond: 40000 });
  const save = async () => {
    try { await api.put("/loyalty/rules", { points_per_mt: Number(ppm), auto_approve: auto, finance_approval_threshold_mt: Number(fin), tier_thresholds: { Silver: 0, Gold: Number(th.Gold), Platinum: Number(th.Platinum), Diamond: Number(th.Diamond) } }); toast.success("Rules updated"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <SectionCard title="Loyalty Rules Engine" testid="loyalty-rules">
      <div className="grid sm:grid-cols-2 gap-5 max-w-2xl">
        <div><label className="text-xs font-semibold text-slate-600">Points per MT dispatched</label><input type="number" value={ppm} onChange={(e) => setPpm(e.target.value)} data-testid="rule-ppm" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mt-1" /></div>
        <div><label className="text-xs font-semibold text-slate-600">Finance approval threshold (MT)</label><input type="number" value={fin} onChange={(e) => setFin(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mt-1" /></div>
        {["Gold", "Platinum", "Diamond"].map((t) => (
          <div key={t}><label className="text-xs font-semibold text-slate-600">{t} tier threshold (pts)</label><input type="number" value={th[t]} onChange={(e) => setTh((s) => ({ ...s, [t]: e.target.value }))} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mt-1" /></div>
        ))}
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} data-testid="rule-auto" /> Auto-approve points on dispatch (skip Rewards Ops gate)</label>
      </div>
      <button data-testid="save-rules" onClick={save} className="mt-5 text-white font-semibold px-5 py-2 rounded-lg copper-glow" style={{ background: "var(--copper)" }}>Save Rules</button>
    </SectionCard>
  );
}

function RedeemDialog({ reward, partners, onClose, onDone }) {
  const [pid, setPid] = useState("");
  const submit = async () => {
    if (!pid) { toast.error("Select partner"); return; }
    try { await api.post("/loyalty/redeem", { reward_id: reward.id, partner_id: pid }); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="redeem-drawer">
        <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-slate-900">Redeem: {reward.name}</h3><button onClick={onClose} className="text-slate-400"><X size={18} /></button></div>
        <p className="text-sm text-slate-500 mb-3">Cost: <b className="font-mono text-[var(--copper)]">{num(reward.points)} pts</b>. Partner must hold enough approved balance.</p>
        <select value={pid} onChange={(e) => setPid(e.target.value)} data-testid="redeem-partner" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4">
          <option value="">Select partner…</option>
          {partners.filter((p) => p.points_balance >= reward.points).map((p) => <option key={p.id} value={p.id}>{p.name} · {p.partner_id} · {num(p.points_balance)} pts</option>)}
        </select>
        <button data-testid="redeem-confirm" onClick={submit} className="w-full text-white font-semibold py-2.5 rounded-lg copper-glow" style={{ background: "var(--copper)" }}>Confirm Redemption</button>
      </div>
    </div>
  );
}

function Tbl({ head, children, empty }) {
  return (<div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">{head.map((h) => <th key={h} className="px-5 py-3">{h}</th>)}</tr></thead><tbody>{children}</tbody></table>{empty && <Empty />}</div>);
}
